import { useMemo, useState } from "react";
import { CalendarRange, Clock, Layers, TrendingDown, TrendingUp, Users } from "lucide-react";
import { BotoesExportacao, ErrorAlert, LoadingSpinner } from "../../components/ui";
import type { GrupoDescritivo, ItemDescritivo } from "../../types/descritivos";
import {
  TODOS_OS_PROJETOS,
  exportarDescritivo,
  useDescritivo,
  useProjetosDescritivo,
  useResumoDescritivo,
} from "./api";
import { fmtData, fmtHoras, labelMes, limitesDoMes, nomeMes, variacao } from "./format";

const hoje = new Date();

const DescritivosPage = () => {
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [projetoId, setProjetoId] = useState(TODOS_OS_PROJETOS);

  const [inicio, fim] = useMemo(() => limitesDoMes(ano, mes), [ano, mes]);

  const descritivo = useDescritivo({ inicio, fim, projetoId });
  const projetos = useProjetosDescritivo();
  const resumo = useResumoDescritivo();

  const dados = descritivo.data;
  const semNadaLancado =
    dados && dados.grupos.length === 0 && (resumo.data?.serie.every((m) => m.horas === 0) ?? false);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-800 via-blue-600 to-blue-500 bg-clip-text text-transparent">
            Descritivo de horas
          </h1>
          <p className="text-gray-600 mt-1">
            Horas lançadas nos seus produtos, mês a mês.
          </p>
        </div>
        <BotoesExportacao
          aoExportar={(formato) => exportarDescritivo({ inicio, fim, projetoId }, formato)}
          desabilitado={descritivo.isLoading || (dados?.grupos.length ?? 0) === 0}
        />
      </div>

      {resumo.data && <Cartoes resumo={resumo.data} aoEscolherMes={(a, m) => { setAno(a); setMes(m); }} mesSelecionado={{ ano, mes }} />}

      {/* Filtros */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-4 flex flex-wrap items-end gap-6">
        <div>
          <label htmlFor="mes" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            <CalendarRange className="w-3.5 h-3.5 inline mr-1" />
            Mês
          </label>
          <div className="flex gap-2">
            <select
              id="mes"
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{nomeMes(m)}</option>
              ))}
            </select>
            <select
              aria-label="Ano"
              value={ano}
              onChange={(e) => setAno(Number(e.target.value))}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
            >
              {[hoje.getFullYear(), hoje.getFullYear() - 1, hoje.getFullYear() - 2].map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="projeto" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            <Layers className="w-3.5 h-3.5 inline mr-1" />
            Produto
          </label>
          <select
            id="projeto"
            value={projetoId}
            onChange={(e) => setProjetoId(Number(e.target.value))}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm min-w-[14rem] focus:border-blue-500 focus:outline-none"
          >
            <option value={TODOS_OS_PROJETOS}>Todos os produtos</option>
            {(projetos.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.nome}</option>
            ))}
          </select>
        </div>

        {descritivo.isFetching && <span className="text-sm text-gray-500">Atualizando…</span>}
      </div>

      {descritivo.isLoading && (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      )}

      {descritivo.isError && (
        <ErrorAlert
          title="Não foi possível carregar o descritivo"
          message={(descritivo.error as Error)?.message ?? "Tente novamente."}
          onRetry={() => descritivo.refetch()}
          variant="error"
        />
      )}

      {dados && !descritivo.isLoading && !descritivo.isError && (
        <>
          {dados.grupos.length > 0 && <ResumoDoPeriodo dados={dados} />}
          <Tabela grupos={dados.grupos} total={dados.total} semNadaLancado={Boolean(semNadaLancado)} />
        </>
      )}
    </div>
  );
};

const Cartoes = ({
  resumo,
  aoEscolherMes,
  mesSelecionado,
}: {
  resumo: { serie: { ano: number; mes: number; horas: number }[]; mesAtual: number; mesAnterior: number };
  aoEscolherMes: (ano: number, mes: number) => void;
  mesSelecionado: { ano: number; mes: number };
}) => {
  const variacaoMes = variacao(resumo.mesAtual, resumo.mesAnterior);
  const maior = Math.max(...resumo.serie.map((m) => m.horas), 1);

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mês atual</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{fmtHoras(resumo.mesAtual)}<span className="text-base font-medium text-gray-500 ml-1">h</span></p>
        {variacaoMes !== null && (
          <p className={["text-sm mt-1 inline-flex items-center gap-1", variacaoMes >= 0 ? "text-green-700" : "text-orange-700"].join(" ")}>
            {variacaoMes >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {variacaoMes >= 0 ? "+" : ""}{variacaoMes}% em relação ao mês anterior
          </p>
        )}
      </div>

      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mês anterior</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{fmtHoras(resumo.mesAnterior)}<span className="text-base font-medium text-gray-500 ml-1">h</span></p>
      </div>

      {/* Cinco meses em barras. Clique navega para o mês. */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Últimos cinco meses</p>
        <div className="flex items-end justify-between gap-2 h-20">
          {resumo.serie.map((m) => {
            const selecionado = m.ano === mesSelecionado.ano && m.mes === mesSelecionado.mes;
            return (
              <button
                key={`${m.ano}-${m.mes}`}
                type="button"
                onClick={() => aoEscolherMes(m.ano, m.mes)}
                title={`${fmtHoras(m.horas)} h em ${nomeMes(m.mes)} de ${m.ano}`}
                className="flex-1 flex flex-col items-center justify-end gap-1 group"
              >
                <span className="text-[10px] text-gray-500 tabular-nums">{m.horas > 0 ? Math.round(m.horas) : ""}</span>
                <span
                  style={{ height: `${Math.max((m.horas / maior) * 100, 3)}%` }}
                  className={["w-full rounded-t transition-colors", selecionado ? "bg-blue-600" : "bg-blue-200 group-hover:bg-blue-400"].join(" ")}
                />
                <span className={["text-[11px]", selecionado ? "text-blue-700 font-semibold" : "text-gray-500"].join(" ")}>
                  {labelMes(m.mes, m.ano)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ResumoDoPeriodo = ({ dados }: { dados: { total: number; resumo: { horas: number; lancamentos: number; projetos: number; executores: number; maiorProjeto: { nome: string; horas: number; percentual: number } | null } } }) => (
  <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-5 flex flex-wrap gap-x-10 gap-y-4">
    <Indicador icone={<Clock className="w-4 h-4" />} rotulo="Horas no período" valor={`${fmtHoras(dados.total)} h`} />
    <Indicador rotulo="Lançamentos" valor={String(dados.resumo.lancamentos)} />
    <Indicador icone={<Layers className="w-4 h-4" />} rotulo="Produtos" valor={String(dados.resumo.projetos)} />
    <Indicador icone={<Users className="w-4 h-4" />} rotulo="Executores" valor={String(dados.resumo.executores)} />
    {dados.resumo.maiorProjeto && (
      <Indicador
        rotulo="Produto com mais horas"
        valor={`${dados.resumo.maiorProjeto.nome} · ${dados.resumo.maiorProjeto.percentual}%`}
      />
    )}
  </div>
);

const Indicador = ({ icone, rotulo, valor }: { icone?: React.ReactNode; rotulo: string; valor: string }) => (
  <div>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
      {icone}
      {rotulo}
    </p>
    <p className="text-lg font-semibold text-gray-900 mt-0.5">{valor}</p>
  </div>
);

const Tabela = ({
  grupos,
  total,
  semNadaLancado,
}: {
  grupos: GrupoDescritivo[];
  total: number;
  semNadaLancado: boolean;
}) => {
  if (grupos.length === 0) {
    return (
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-12 text-center">
        <p className="text-gray-600 font-medium">
          {semNadaLancado
            ? "Ainda não há horas lançadas para o seu cliente."
            : "Nenhuma hora lançada neste mês."}
        </p>
        <p className="text-gray-500 text-sm mt-1">
          {semNadaLancado
            ? "Quando a equipe registrar atendimentos, eles aparecem aqui."
            : "Escolha outro mês ou outro produto."}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50/80 text-gray-600">
            <tr>
              <th className="text-left font-semibold px-4 py-3">Data</th>
              <th className="text-left font-semibold px-4 py-3">Horário</th>
              <th className="text-left font-semibold px-4 py-3">Executor</th>
              <th className="text-left font-semibold px-4 py-3">Descrição</th>
              <th className="text-right font-semibold px-4 py-3">Horas</th>
            </tr>
          </thead>
          <tbody>
            {grupos.map((g) => (
              <GrupoLinhas key={g.idProjeto} grupo={g} total={total} />
            ))}
            <tr className="bg-blue-50/70 border-t-2 border-blue-100">
              <td colSpan={4} className="px-4 py-3 font-semibold text-gray-800 text-right">
                Total do período
              </td>
              <td className="px-4 py-3 text-right font-bold text-blue-800 tabular-nums">
                {fmtHoras(total)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GrupoLinhas = ({ grupo, total }: { grupo: GrupoDescritivo; total: number }) => (
  <>
    <tr className="bg-gray-50/60 border-t border-gray-200">
      <td colSpan={4} className="px-4 py-2.5">
        <span className="font-semibold text-gray-800">{grupo.projeto}</span>
        <span className="ml-2 text-xs text-gray-500">
          {grupo.itens.length} lançamento(s)
        </span>
      </td>
      <td className="px-4 py-2.5 text-right">
        <span className="inline-flex items-center gap-2 justify-end">
          <span className="h-1.5 w-16 rounded-full bg-gray-200 overflow-hidden">
            <span
              className="block h-full rounded-full bg-blue-500"
              style={{ width: `${total > 0 ? (grupo.subtotal / total) * 100 : 0}%` }}
            />
          </span>
          <span className="font-semibold text-gray-800 tabular-nums">{fmtHoras(grupo.subtotal)}</span>
        </span>
      </td>
    </tr>
    {grupo.itens.map((item, i) => (
      <LinhaItem key={`${grupo.idProjeto}-${i}`} item={item} />
    ))}
  </>
);

const LinhaItem = ({ item }: { item: ItemDescritivo }) => {
  // Expediente parcial: a linha aparece, mas sem as horas. Elas continuam no
  // subtotal do projeto, que vem somado do servidor.
  const parcial = item.visibilidadeExpediente?.trim() === "PRC";

  const partes = [
    item.chExtra > 0 ? `${fmtHoras(item.chExtra)} extra` : null,
    item.chViagem > 0 ? `${fmtHoras(item.chViagem)} viagem` : null,
  ].filter(Boolean);

  return (
    <tr className="border-t border-gray-100 hover:bg-blue-50/30">
      <td className="px-4 py-2 text-gray-600 whitespace-nowrap tabular-nums">
        {fmtData(item.dataExecucao)}
      </td>
      <td className="px-4 py-2 text-gray-600 whitespace-nowrap tabular-nums">{item.horario ?? "—"}</td>
      <td className="px-4 py-2 text-gray-600">{item.executor ?? "—"}</td>
      <td className="px-4 py-2 text-gray-800">{item.descricao ?? "—"}</td>
      <td className="px-4 py-2 text-right tabular-nums">
        {parcial ? (
          <span className="text-gray-400" title="Expediente de visibilidade parcial">—</span>
        ) : (
          <>
            <span className="font-medium text-gray-800">{fmtHoras(item.horasTotais)}</span>
            {partes.length > 0 && (
              <span className="block text-[11px] text-gray-500">
                {fmtHoras(item.cargaHoraria)} + {partes.join(" + ")}
              </span>
            )}
          </>
        )}
      </td>
    </tr>
  );
};

export default DescritivosPage;
